// src/components/Colegiado/ColegiadoRecibos.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  Paper, Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, CircularProgress, Divider, MenuItem, Select, FormControl, InputLabel,
  Button, Alert, Stack
} from "@mui/material";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";

/* ================= helpers ================= */
function getYearFromPeriodo(x) {
  if (typeof x?.periodo === "string" && /^\d{4}q\d$/i.test(x.periodo)) {
    return parseInt(x.periodo.slice(0, 4), 10);
  }
  return null;
}

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

function formatFechaLargaES(date = new Date()) {
  const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
    "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  const dia = date.getDate();
  const mes = meses[date.getMonth()];
  const anio = date.getFullYear();
  return `Valladolid, a ${dia} de ${mes} de ${anio}`;
}

function parseImporte(x) {
  const v = Number(x?.importe ?? x?.importeEuros ?? 0);
  return Number.isFinite(v) ? v : 0;
}

function getEstadoRecibo(r) {
  return String(r.resultado_banco ?? r.resultadoBanco ?? "PENDIENTE").toUpperCase();
}

function isReciboCobrada(r) {
  return getEstadoRecibo(r) === "COBRADO";
}

function estadoColorRecibo(est) {
  return est === "COBRADO" ? "success" : est === "PENDIENTE" ? "warning" : "error";
}

/**
 * Devuelve TRUE si la devolución debe descontar (pagada=1 o pagada=2).
 * OJO: en tu tabla devoluciones existe la columna "pagada" (0/1/2).
 */
function isDevolucionDescontable(d) {
  const p = Number(d?.pagada ?? d?.pagadaFlag ?? d?.estado ?? 0);
  return p === 1 || p === 2;
}

/** Forzar que las plantillas se carguen SIEMPRE desde el mismo origen que la SPA */
const buildPlantillaUrl = (path) => {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;

  const origin = window.location.origin.replace(/\/+$/, "");
  const cleanPath = String(path).replace(/^\/+/, "");
  return `${origin}/${cleanPath}`;
};

/* ================ componente ================= */
export default function ColegiadoRecibos({ codigo }) {
  const API = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const token =
    localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY || "token") ||
    localStorage.getItem("JWT_TOKEN") ||
    localStorage.getItem("token");

  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  const userCode = useMemo(() => {
    if (codigo) return codigo;
    try {
      const dec = token ? jwtDecode(token) : {};
      return dec?.codigo ?? dec?.colegiadoCodigo ?? dec?.colegiado_codigo ?? dec?.id ?? null;
    } catch {
      return null;
    }
  }, [codigo, token]);

  const [devoluciones, setDevoluciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [hist, setHist] = useState({
    colegiado_codigo: null,
    nombre: "",
    apellido_1: "",
    apellido_2: "",
    nif: "",
    direccion: "",
    c_p_: "",
    localidad: "",
    provincia: "",
    recibos: [],
  });

  const [anioSelState, setAnioSel] = useState(new Date().getFullYear());
  const [genBusy, setGenBusy] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [warnMsg, setWarnMsg] = useState("");

  // Carga datos (perfil + historial + devoluciones) sin depender de anioSelState
  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr(null);

      if (!userCode) {
        setErr("No se encuentra el código de colegiado.");
        setLoading(false);
        return;
      }

      try {
        const [perfilRes, histRes, devRes] = await Promise.all([
          axios.get(`${API}/colegiado/perfil`, { headers: authHeader() }).catch(() => ({ data: null })),
          axios.get(`${API}/api/colegiados/${encodeURIComponent(userCode)}/historial`, { headers: authHeader() }),
          axios.get(`${API}/api/colegiados/${encodeURIComponent(userCode)}/devoluciones`, { headers: authHeader() })
            .catch(() => ({ data: [] })),
        ]);

        if (!alive) return;

        const perfil = perfilRes?.data ?? null;
        const raw = histRes?.data;

        // Normalizar historial
        const baseHist = Array.isArray(raw)
          ? {
              colegiado_codigo: userCode,
              nombre: "",
              apellido_1: "",
              apellido_2: "",
              nif: raw.length ? (raw[0]?.nif ?? "") : "",
              direccion: raw.length ? (raw[0]?.direccion ?? "") : "",
              c_p_: raw.length ? (raw[0]?.c_p_ ?? raw[0]?.cp ?? "") : "",
              localidad: raw.length ? (raw[0]?.localidad ?? "") : "",
              provincia: raw.length ? (raw[0]?.provincia ?? "") : "",
              recibos: raw
            }
          : {
              colegiado_codigo: raw?.colegiadoCodigo ?? raw?.colegiado_codigo ?? userCode,
              nombre: raw?.nombre ?? "",
              apellido_1: raw?.apellido_1 ?? raw?.apellido1 ?? "",
              apellido_2: raw?.apellido_2 ?? raw?.apellido2 ?? "",
              nif: raw?.nif ?? "",
              direccion: raw?.direccion ?? "",
              c_p_: raw?.c_p_ ?? raw?.cp ?? "",
              localidad: raw?.localidad ?? "",
              provincia: raw?.provincia ?? "",
              recibos: Array.isArray(raw?.recibos) ? raw.recibos : []
            };

        // Mezclar con PERFIL dando prioridad a los campos del perfil si existen
        const merged = {
          ...baseHist,
          nombre: (perfil?.nombre ?? perfil?.name ?? baseHist.nombre) || "",
          apellido_1: (perfil?.apellido_1 ?? perfil?.apellido1 ?? baseHist.apellido_1) || "",
          apellido_2: (perfil?.apellido_2 ?? perfil?.apellido2 ?? baseHist.apellido_2) || "",
          nif: (perfil?.nif ?? perfil?.dni ?? baseHist.nif) || "",
          direccion: (perfil?.direccion ?? baseHist.direccion) || "",
          c_p_: (perfil?.c_p_ ?? perfil?.cp ?? baseHist.c_p_) || "",
          localidad: (perfil?.localidad ?? baseHist.localidad) || "",
          provincia: (perfil?.provincia ?? baseHist.provincia) || "",
        };

        setHist(merged);

        const devs = Array.isArray(devRes?.data) ? devRes.data : [];
        setDevoluciones(devs);

        // seleccionar año por defecto: actual si existe; si no, máximo disponible
        const ys = new Set((merged.recibos || []).map(getYearFromPeriodo).filter(Boolean));
        const currentYear = new Date().getFullYear();
        if (ys.size > 0) {
          const maxY = Math.max(...ys);
          if (!ys.has(anioSelState)) {
            setAnioSel(ys.has(currentYear) ? currentYear : maxY);
          }
        }
      } catch (e) {
        console.error(e);
        setErr("No se pudo cargar el historial de recibos.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [API, token, userCode]); // sin anioSelState

  const recibos = useMemo(() => (Array.isArray(hist.recibos) ? hist.recibos : []), [hist.recibos]);

  const aniosDisponibles = useMemo(() => {
    const ys = new Set();
    for (const r of recibos) {
      const y = getYearFromPeriodo(r);
      if (y) ys.add(y);
    }
    if (ys.size === 0) ys.add(new Date().getFullYear());
    return Array.from(ys).sort((a, b) => b - a);
  }, [recibos]);

  const recibosAnio = useMemo(() => {
    return recibos.filter(r => getYearFromPeriodo(r) === anioSelState);
  }, [recibos, anioSelState]);

  /**
   * Bloqueo de certificado si hay PENDIENTE o PENDIENTE_DEVUELTO en el año seleccionado.
   * (Las devoluciones NO cambian este bloqueo, solo ajustan el neto).
   */
  const alDiaEnAnio = useMemo(
    () => !recibosAnio.some(r => {
      const e = getEstadoRecibo(r);
      return e === "PENDIENTE" || e === "PENDIENTE_DEVUELTO";
    }),
    [recibosAnio]
  );

  const displayName = useMemo(() => {
    const parts = [
      (hist.nombre || "").toString().trim(),
      (hist.apellido_1 || "").toString().trim(),
      (hist.apellido_2 || "").toString().trim(),
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : `Colegiado ${hist.colegiado_codigo ?? userCode}`;
  }, [hist, userCode]);

  // ===== Devoluciones del año (por periodo) =====
  const devolucionesAnio = useMemo(() => {
    return (Array.isArray(devoluciones) ? devoluciones : []).filter(d => getYearFromPeriodo(d) === anioSelState);
  }, [devoluciones, anioSelState]);

  // Total devuelto SOLO si pagada=1 o pagada=2
  const totalDevueltoAplicableAnio = useMemo(() => {
    return devolucionesAnio.reduce((acc, d) => {
      return acc + (isDevolucionDescontable(d) ? parseImporte(d) : 0);
    }, 0);
  }, [devolucionesAnio]);

  // Total cobrado (solo recibos COBRADO)
  const totalCobradosAnio = useMemo(() => {
    return recibosAnio.reduce((acc, r) => acc + (isReciboCobrada(r) ? parseImporte(r) : 0), 0);
  }, [recibosAnio]);

  // Neto = cobrados - devoluciones aplicables
  const totalNetoAnio = useMemo(() => {
    return totalCobradosAnio - totalDevueltoAplicableAnio;
  }, [totalCobradosAnio, totalDevueltoAplicableAnio]);

  const sumaConComa = useMemo(() => totalNetoAnio.toFixed(2).replace(".", ","), [totalNetoAnio]);

  // ======= Generar PDF vía backend =======
  async function generarPDFviaBackend() {
    setWarnMsg("");
    setOkMsg("");

    if (!alDiaEnAnio) {
      setWarnMsg("No se puede emitir el certificado: hay recibos PENDIENTE/PENDIENTE_DEVUELTO en el año seleccionado.");
      return;
    }

    setGenBusy(true);
    try {
      const plantillaUrl = buildPlantillaUrl("/certificados/certificado_cuotas.docx");
      console.debug("[Recibos] Cargando plantilla desde:", plantillaUrl);

      const resp = await fetch(plantillaUrl, { cache: "no-store" });
      if (!resp.ok) throw new Error(`No se encontró la plantilla en ${plantillaUrl}`);

      const arrayBuffer = await resp.arrayBuffer();
      const zip = new PizZip(arrayBuffer);

      const data = {
        NOMBRE_COMPLETO: displayName,
        DIRECCION: hist.direccion ?? "",
        "C_P": hist.c_p_ ?? hist.cp ?? "",
        LOCALIDAD: hist.localidad ?? "",
        PROVINCIA: hist.provincia ?? "",
        NIF: hist.nif ?? "",
        anio: String(anioSelState),
        ANIO: String(anioSelState),

        // IMPORTANTE: SUMA ya es NETO (cobrado - devoluciones pagadas/compensadas)
        SUMA: sumaConComa,

        FECHA_LARGA: formatFechaLargaES(new Date()),
      };

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        delimiters: { start: "{", end: "}" },
        nullGetter: () => ""
      });

      doc.setData(data);
      doc.render();

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const form = new FormData();
      const nombreBase = `Certificado_Cuotas_${anioSelState}_${(displayName || "Colegiado").replace(/\s+/g, "_")}`;
      form.append("file", docxBlob, `${nombreBase}.docx`);

      const pdfResp = await axios.post(
        `${API}/api/convert/docx-to-pdf`,
        form,
        {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
          responseType: "blob",
        }
      );

      const pdfBlob = new Blob([pdfResp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${nombreBase}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setOkMsg("Certificado PDF generado correctamente por el servidor.");
    } catch (e) {
      console.error(e);
      setWarnMsg(e?.response?.data || e?.message || "No se pudo convertir el DOCX a PDF.");
    } finally {
      setGenBusy(false);
    }
  }

  /* ======================== RENDER ======================== */
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Recibos de {displayName}
      </Typography>

      <Typography variant="h6" gutterBottom>
        Certificado de Cuotas
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="anio-label">Año</InputLabel>
          <Select
            labelId="anio-label"
            label="Año"
            value={anioSelState}
            onChange={(e) => setAnioSel(Number(e.target.value))}
          >
            {aniosDisponibles.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body1">
          Total cobrado {anioSelState}: <b>{totalCobradosAnio.toFixed(2)} €</b>
          {totalDevueltoAplicableAnio > 0 && (
            <> — Devolución: <b>{totalDevueltoAplicableAnio.toFixed(2)} €</b></>
          )}
          <> — Abonado final: <b>{totalNetoAnio.toFixed(2)} €</b></>
        </Typography>

        {!alDiaEnAnio && (
          <Chip
            color="warning"
            label="Debe regularizar su situación para poder descargar el certificado"
          />
        )}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={generarPDFviaBackend}
          disabled={!alDiaEnAnio || genBusy}
          sx={{ alignSelf: "flex-start", width: "auto" }}
        >
          {genBusy ? "Generando…" : "Generar Certificado"}
        </Button>
      </Stack>

      <Divider sx={{ my: 3 }} />

      {loading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={20} /> <span>Cargando recibos…</span>
        </Box>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {!loading && !err && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha cobro</TableCell>
                <TableCell>Periodo</TableCell>
                <TableCell>Concepto</TableCell>
                <TableCell align="right">Importe (€)</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recibos.map((r) => {
                const fecha = r.reqd_colltn_dt ?? r.reqdColltnDt ?? r.devolucion_fecha ?? r.creado_el ?? r.creadoEl ?? "";
                const importe = parseImporte(r);
                const estado = getEstadoRecibo(r);
                return (
                  <TableRow key={r.id ?? `${r.periodo}-${importe}-${fecha}`}>
                    <TableCell>{fmtDate(fecha)}</TableCell>
                    <TableCell>{r.periodo || ""}</TableCell>
                    <TableCell>{r.concepto || ""}</TableCell>
                    <TableCell align="right">{importe.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={estado} color={estadoColorRecibo(estado)} variant="outlined" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {recibos.length === 0 && (
            <Box mt={2}>
              <Typography variant="body2">No hay recibos para mostrar.</Typography>
            </Box>
          )}

          {okMsg && (
            <Alert sx={{ mt: 2 }} severity="success" onClose={() => setOkMsg("")}>
              {okMsg}
            </Alert>
          )}
          {warnMsg && (
            <Alert sx={{ mt: 2 }} severity="warning" onClose={() => setWarnMsg("")}>
              {warnMsg}
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
}
